'use strict';

/**
 * Utility functions for handling eating patterns
 */

/**
 * Filters out hidden eating patterns
 * @param {Array} patterns - Array of eating patterns from API
 * @returns {Array} - Array of visible patterns only
 */
export const filterVisiblePatterns = (patterns = []) => {
    return patterns.filter((pattern) => !pattern.hidden);
};

/**
 * Calculates the total time value for an eating pattern's meal types.
 * Uses the sum of all meal begin times as a proxy for "earliness" of the pattern.
 * @param {Array} mealTypes - Array of meal type objects with begin property (minutes from midnight)
 * @returns {number} - Sum of all begin times, or 0 if no valid times
 */
const getTotalMealTime = (mealTypes = []) => {
    return mealTypes.reduce((sum, meal) => {
        return sum + (typeof meal.begin === 'number' ? meal.begin : 0);
    }, 0);
};

/**
 * Sorts eating patterns by:
 * 1. Number of meals (ascending: 2, 3, 4, 5, 6 meals)
 * 2. Within same meal count, by time of day (patterns with earlier meals first)
 *
 * @param {Array} patterns - Array of eating patterns to sort
 * @returns {Array} - New sorted array of patterns (does not mutate original)
 */
export const sortEatingPatterns = (patterns = []) => {
    return [...patterns].sort((a, b) => {
        const aMealCount = a.meal_types?.length || 0;
        const bMealCount = b.meal_types?.length || 0;

        // First, sort by number of meals (ascending)
        if (aMealCount !== bMealCount) {
            return aMealCount - bMealCount;
        }

        // If same meal count, sort by total meal time (earlier patterns first)
        const aTotalTime = getTotalMealTime(a.meal_types);
        const bTotalTime = getTotalMealTime(b.meal_types);

        return aTotalTime - bTotalTime;
    });
};

/**
 * Checks if an eating pattern is available in the list
 * @param {string} patternName - Name of the pattern to check
 * @param {Array} availablePatterns - Array of available patterns
 * @returns {boolean} - True if pattern is available
 */
export const isPatternAvailable = (patternName, availablePatterns = []) => {
    return availablePatterns.some((pattern) => pattern.name === patternName);
};

/**
 * Finds an eating pattern by name
 * @param {string} patternName - Name of the pattern to find
 * @param {Array} availablePatterns - Array of available patterns
 * @returns {Object|null} - Pattern object or null if not found
 */
export const findPatternByName = (patternName, availablePatterns = []) => {
    return availablePatterns.find((pattern) => pattern.name === patternName) || null;
};

/**
 * Artificially includes current pattern in available patterns if it's disabled/hidden
 * This allows users to see their current selection even if it's no longer available
 * @param {string} currentPatternName - Current pattern name
 * @param {Array} allPatterns - All patterns from API (including hidden)
 * @param {Array} availablePatterns - Filtered visible patterns
 * @param {Array} currentMealTypes - Current meal_types from patient preferences (for creating virtual pattern)
 * @returns {Array} - Available patterns with current pattern included if needed
 */
export const includeCurrentPatternIfDisabled = (currentPatternName, allPatterns = [], availablePatterns = [], currentMealTypes = []) => {
    if (!currentPatternName) {
        return availablePatterns;
    }

    // Check if current pattern is already in available patterns
    const isCurrentAvailable = isPatternAvailable(currentPatternName, availablePatterns);

    if (isCurrentAvailable) {
        return availablePatterns;
    }

    // Find current pattern in all patterns (including hidden)
    let currentPattern = findPatternByName(currentPatternName, allPatterns);

    // If not found in allPatterns, create a virtual pattern from currentMealTypes
    // This happens when backend filters out disabled patterns before sending to frontend
    if (!currentPattern && currentMealTypes.length > 0) {
        currentPattern = {
            name: currentPatternName,
            meal_types: currentMealTypes,
            hidden: true, // Mark as hidden since it wasn't in allPatterns
        };
    }

    if (!currentPattern) {
        return availablePatterns;
    }

    // Mark pattern as disabled and include it
    return [{ ...currentPattern, disabled: true }, ...availablePatterns];
};

/**
 * Selects the appropriate eating pattern based on current preferences
 * @param {Object} options - Configuration options
 * @param {Array} options.allPatterns - All eating patterns from API response
 * @param {string} options.currentPatternName - Currently selected pattern name
 * @param {string} options.fallbackPatternName - Fallback pattern from API resolve
 * @param {boolean} options.includeDisabledCurrent - If true, includes current pattern even if disabled
 * @param {Array} options.currentMealTypes - Current meal_types from patient preferences (for creating virtual pattern)
 * @returns {Object} - Selected pattern information
 * @returns {string} result.selectedPatternName - Name of selected pattern
 * @returns {Array} result.selectedMealTypes - Meal types for selected pattern
 * @returns {Array} result.availablePatterns - All visible patterns (for dropdown)
 * @returns {boolean} result.isCurrentPatternDisabled - True if selected pattern is disabled
 * @throws {Error} - If no patterns are available
 */
export const selectEatingPattern = ({
    allPatterns = [],
    currentPatternName = null,
    fallbackPatternName = null,
    includeDisabledCurrent = false,
    currentMealTypes = [],
}) => {
    // Filter out hidden patterns and sort by meal count then time of day
    let availablePatterns = sortEatingPatterns(filterVisiblePatterns(allPatterns));

    // If includeDisabledCurrent is true, artificially add current pattern if it's disabled
    if (includeDisabledCurrent) {
        availablePatterns = includeCurrentPatternIfDisabled(currentPatternName, allPatterns, availablePatterns, currentMealTypes);
    }

    // Edge case: no patterns available
    if (availablePatterns.length === 0) {
        throw new Error('No eating patterns available for this nutrition pattern');
    }

    // Try to use current pattern name first
    const patternNameToCheck = currentPatternName || fallbackPatternName;

    let selectedPattern;
    let isCurrentPatternDisabled = false;

    if (patternNameToCheck && isPatternAvailable(patternNameToCheck, availablePatterns)) {
        // Current pattern is available, use it
        selectedPattern = findPatternByName(patternNameToCheck, availablePatterns);
        isCurrentPatternDisabled = selectedPattern.disabled === true;
    } else {
        // Current pattern not available, use first visible one
        selectedPattern = availablePatterns[0];
    }

    return {
        selectedPatternName: selectedPattern.name,
        selectedMealTypes: selectedPattern.meal_types,
        availablePatterns,
        isCurrentPatternDisabled,
    };
};

/**
 * Creates options array for dropdown from eating patterns
 * @param {Array} patterns - Array of eating patterns
 * @param {Function} labelFormatter - Function to format the label (receives meal_types)
 * @returns {Array} - Array of {value, label} objects for Select component
 */
export const createPatternOptions = (patterns = [], labelFormatter = null) => {
    return patterns.map((pattern) => ({
        value: pattern.name,
        label: labelFormatter ? labelFormatter(pattern.meal_types) : pattern.name,
    }));
};

/**
 * Overlays freshly resolved nutrient envelopes onto existing meal types while
 * preserving their timings (begin/end) and other customized metadata. Meals are
 * matched by name (the key the resolver uses). If the structures don't line up,
 * the eating pattern has effectively changed and we fall back to the resolved
 * meal types.
 *
 * @param {Array} existingMealTypes - Current meal types from preferences
 * @param {Array} resolvedMealTypes - Meal types from the resolve response
 * @returns {{mealTypes: Array, preserved: boolean}} - Merged meal types and whether
 *          the existing structure was preserved
 */
const mergeMealTypeEnvelopes = (existingMealTypes, resolvedMealTypes) => {
    if (!Array.isArray(existingMealTypes) || existingMealTypes.length === 0) {
        return { mealTypes: resolvedMealTypes, preserved: false };
    }

    if (!Array.isArray(resolvedMealTypes) || resolvedMealTypes.length === 0) {
        return { mealTypes: existingMealTypes, preserved: true };
    }

    const resolvedByName = new Map(resolvedMealTypes.map((mealType) => [mealType.name, mealType]));

    // Require the same set of meals on both sides (meal names are unique within a
    // pattern). A different count or a missing match means the eating pattern
    // diverged, so we can't safely preserve timings.
    const structureMatches = existingMealTypes.length === resolvedMealTypes.length &&
        existingMealTypes.every((mealType) => resolvedByName.has(mealType.name));

    if (!structureMatches) {
        return { mealTypes: resolvedMealTypes, preserved: false };
    }

    const mealTypes = existingMealTypes.map((existingMealType) => ({
        ...existingMealType,
        envelope: resolvedByName.get(existingMealType.name).envelope,
    }));

    return { mealTypes, preserved: true };
};

/**
 * Merges a freshly resolved preferences object into the existing preferences,
 * preserving pro/user-customized meal metadata (meal timings, eating pattern,
 * leftovers setting) that the resolve endpoint always recomputes from template
 * defaults.
 *
 * The /resolve endpoint is a stateless resolver: it rebuilds meal_types (including
 * begin/end times) from the nutrition pattern template and has no knowledge of the
 * customizations a pro made. A blanket spread of the resolved response therefore
 * wipes out manually-set meal timing ranges whenever an unrelated input (e.g. the
 * calorie deficit / target energy) changes. Energy changes only legitimately affect
 * the nutrient envelopes (daily_totals and each meal's envelope), so we overlay just
 * those and keep everything else as the pro left it.
 *
 * @param {Object} existing - The patient/profile's current preferences
 * @param {Object} resolved - The preferences object returned by /resolve
 * @returns {Object} - Merged preferences
 */
export const mergeResolvedPreferences = (existing, resolved) => {
    if (!existing) {
        return resolved;
    }

    if (!resolved) {
        return existing;
    }

    // Start from a shallow merge so energy-dependent fields (daily_totals,
    // available_eating_patterns, portion_size_brackets, avoidances) come from the
    // resolved response.
    const merged = { ...existing, ...resolved };

    const { mealTypes, preserved } = mergeMealTypeEnvelopes(existing.meal_types, resolved.meal_types);
    merged.meal_types = mealTypes;

    // Only retain the existing eating pattern + leftovers setting when the meal
    // structure was actually preserved. If the structure genuinely changed (e.g. a
    // different condition/pattern), defer to the resolved values to avoid an
    // inconsistent eating_pattern_name / meal_types pairing.
    if (preserved) {
        if (existing.eating_pattern_name) {
            merged.eating_pattern_name = existing.eating_pattern_name;
        }

        if (existing.leftovers_enabled !== undefined) {
            merged.leftovers_enabled = existing.leftovers_enabled;
        }
    }

    return merged;
};
